import React, { useEffect, useState } from 'react';
import { Grid, Icon, InputAdornment, TextField, Theme, Typography, Box, IconButton, Tooltip, TooltipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { payRateModStyles } from '../PayRateMod.styles';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  ContractChargeAmount,
  ContractRegularRate,
  ContractShift,
  additionalPremiumPay,
  amountPerMile,
  chargePremium,
  onCallRate,
  preceptorRate,
} from 'app/models/Placement/PlacementMods';
import infoIconSvg from 'app/assets/images/infoIcon.svg';
import { FormInputDropdown } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDropdown';
import { getCalculatedAmount } from '../PayFactor/OvertimeFactor';
import { useSelector } from 'react-redux';
import { selectAdditionalPremiumPay } from 'store/redux-store/placement-mods/selectors';
import { validDecimal } from 'app/helpers/numberHelper';
import { makeStyles } from 'tss-react/mui';
import { ChargeRateTooltip } from '../../../CommonComponents/ChargeRateTooltip';

export const infoIcon = (
  <Icon>
    <img src={infoIconSvg} style={{ marginBottom: '22px' }} />
  </Icon>
);

export const AdditionalCompensation = ({ isExpanded, isModEdit }: { isExpanded: boolean; isModEdit: boolean }) => {
  const { t } = useTranslation();
  const { classes } = payRateModStyles({ isExpanded });
  const { control, getValues, setValue } = useFormContext();
  const premiumPayOptions = useSelector(selectAdditionalPremiumPay);
  const additionalPremiumPayValue = useWatch<any>({ name: additionalPremiumPay });
  const onCallRateValue = useWatch<number>({ name: onCallRate });
  const chargePremiumValue = useWatch<number>({ name: chargePremium });
  const preceptorRateValue = useWatch<number>({ name: preceptorRate });
  const amountPerMileValue = useWatch<number>({ name: amountPerMile });
  const contractRegularRate = useWatch<number>({ name: ContractRegularRate });
  const contractShift = useWatch<number>({ name: ContractShift });
  const contractChargeAmount = useWatch<number>({ name: ContractChargeAmount });
  
  
  const handleStatusCodeChange = value => {
    setValue(additionalPremiumPay, value);
  };

  useEffect(() => {
    const value = premiumPayOptions?.find(
      item => parseFloat(item.Description) === additionalPremiumPayValue,
    )?.Description;
    setValue(additionalPremiumPay, value ?? null);
  }, [premiumPayOptions]);

  return (
    <Grid style={{ marginTop: '24px' }}>
      <div>
        <Typography component="span" className={classes.headings}>{`${t(
          'placement.placementMods.additionalCompensation',
        )}`}</Typography>
      </div>
      <Grid className={classes.flex}>
        <Grid className={classes.firstAdditionalField}>
          <Controller
            name={onCallRate}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={onCallRateValue}
                variant="filled"
                className={classes.additionalTextfields}
                label={`${t(`placement.placementMods.${onCallRate}`)}`}
                onChange={e => {
                  if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                    setValue(onCallRate, e.target.value);
                    onChange(e.target.value);
                  }
                }}
                InputLabelProps={{
                  shrink: getValues(onCallRate) ?? false,
                }}
                InputProps={{
                  readOnly: !isModEdit,
                  style: {
                    pointerEvents: isModEdit ? 'auto' : 'none',
                  },
                  startAdornment: (
                    <>
                      {getValues(onCallRate) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMargin}>
          <Controller
            name={chargePremium}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={chargePremiumValue}
                variant="filled"
                className={classes.additionalTextfields}
                label={`${t(`placement.placementMods.${chargePremium}`)}`}
                helperText={
                  <ChargeRateTooltip
                    contractRegularRate={contractRegularRate}
                    contractChargeAmount={contractChargeAmount}
                    contractShift={contractShift}
                    regularRateTitle={t('placement.placementMods.chargeRateTooltip.payrollRegularRate')}
                    chargeAmountTitle={t('placement.placementMods.chargeRateTooltip.payrollChargeAmount')}
                    shiftTitle={t('placement.placementMods.chargeRateTooltip.payrollShift')}
                  />
                }
                onChange={e => {
                  if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                    setValue(chargePremium, e.target.value);
                    onChange(e.target.value);
                  }
                }}
                InputLabelProps={{
                  shrink: getValues(chargePremium) ?? false,
                }}
                InputProps={{
                  readOnly: !isModEdit,
                  style: {
                    pointerEvents: isModEdit ? 'auto' : 'none',
                  },
                  startAdornment: (
                    <>
                      {getValues(chargePremium) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMargin}>
          <Controller
            name={amountPerMile}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={amountPerMileValue ?? ''}
                variant="filled"
                className={classes.additionalTextfields}
                InputLabelProps={{
                  shrink: getValues(amountPerMile) ?? false,
                }}
                label={`${t(`placement.placementMods.${amountPerMile}`)}`}
                onChange={e => {
                  const validInput = e.target.value.replace(/^\./, '0.');
                  if (
                    validInput === '' ||
                    validDecimal(validInput, { numberLength: 6, decimalLength: 3 })
                  ) {
                    const valueToSet =
                      validInput === '' || validInput.endsWith('.')
                        ? validInput
                        : parseFloat(validInput);
                    setValue(amountPerMile, valueToSet);
                    onChange(valueToSet);
                  }
                }}
                InputProps={{
                  readOnly: !isModEdit,
                  style: {
                    pointerEvents: isModEdit ? 'auto' : 'none',
                  },
                  startAdornment: (
                    <>
                      {getValues(amountPerMile) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid className={classes.flex}>
        <Grid className={classes.firstAdditionalField}>
          <Controller
            name={preceptorRate}
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <TextField
                value={preceptorRateValue}
                sx={{ width: isExpanded ? '300px' : '249px' }}
                InputLabelProps={{
                  shrink: getValues(preceptorRate) ?? false,
                }}
                className={classes.additionalTextfields}
                variant="filled"
                label={`${t(`placement.placementMods.${preceptorRate}`)}`}
                onChange={e => {
                  if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                    setValue(preceptorRate, parseFloat(e.target.value));
                    onChange(e.target.value);
                  }
                }}
                InputProps={{
                  readOnly: !isModEdit,
                  style: {
                    pointerEvents: isModEdit ? 'auto' : 'none',
                  },
                  startAdornment: (
                    <>
                      {getValues(preceptorRate) && (
                        <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                      )}
                    </>
                  ),
                  sx: { fontSize: '14px' },
                }}
                {...rest}
              />
            )}
          />
        </Grid>
        <Grid className={classes.factorMargin} sx={{ width: isExpanded ? '300px' : '249px' }}>
          <Controller
            control={control}
            name={additionalPremiumPay}
            render={({ field: { onChange, ...rest } }) => {
              return (
              <FormInputDropdown
                name={additionalPremiumPay}
                label={`${t(`placement.placementMods.${additionalPremiumPay}`)}`}
                  require={false}
                  customClass={classes.additionalTextfields}
                  standard={true}
                  options={premiumPayOptions?.length > 0 ? premiumPayOptions : []}
                  inputValue={getValues(additionalPremiumPay) ?? null}
                  readOnly={!isModEdit}
                  helperText={getCalculatedAmount(getValues, additionalPremiumPay)}
                  handleChange={e => handleStatusCodeChange(e.target.value)}
                ></FormInputDropdown>
              );
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

//@Team @Dheeraj  - use the word 'useStyles' for style hooks and use camel case for functions
const StyledTootltip = makeStyles()((theme: Theme) => ({
  arrow: {
    '&:before': {
      border: '1px solid #E3DFDF',
    },
    color: '#FFFFFF',
  },
  tooltip: {
    minWidth: '6rem',
    fontFamily: 'Roboto',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 'normal',
    color: '#555555',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E3DFDF',
  },
}));

export const CustomTooltip = (props: TooltipProps) => {
  const { classes } = StyledTootltip();

  return <Tooltip arrow enterDelay={500} classes={classes} {...props} />;
};
